import ky from 'ky';
import { LoginResponse, TokenRefreshRequest } from './api/data-contracts';
import { clearAuthTokens, getAccessToken, getAuthTokens, saveAuthTokens } from './auth';
import { getServerHealthy } from './health-state';
import keycloak from './keycloak';

declare global {
  interface Window {
    __ENV__?: {
      API_BASE_URL?: string;
      KEYCLOAK_URL?: string;
      KEYCLOAK_REALM?: string;
    };
  }
}

// Base API configuration (runtime env.js > Vite env > default)
const API_BASE_URL =
  (typeof window !== 'undefined' && window.__ENV__?.API_BASE_URL) ||
  import.meta.env.VITE_API_URL ||
  'http://localhost:8080';

// Debug: 환경변수 확인을 위한 로그 (개발 환경에서만)
if (import.meta.env.DEV) {
  console.log('🔧 API Configuration Debug:');
  console.log(
    'Runtime API_BASE_URL (window.__ENV__):',
    typeof window !== 'undefined' ? window.__ENV__?.API_BASE_URL : undefined
  );
  console.log('VITE_API_URL from env:', import.meta.env.VITE_API_URL);
  console.log('Final API_BASE_URL:', API_BASE_URL);
}

// 토큰 갱신 상태 관리
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// 토큰 갱신 함수
const refreshAccessToken = async (): Promise<boolean> => {
  // 이미 갱신 중이면 동일한 Promise 반환
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const tokens = getAuthTokens();
      if (!tokens) {
        return false;
      }

      const refreshData: TokenRefreshRequest = {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      };

      // refresh 요청은 토큰 갱신을 위한 것이므로 별도 ky 인스턴스 사용
      const response = await ky.post(`${API_BASE_URL}/auth/refresh`, {
        json: refreshData,
        headers: {
          'user-agent': navigator.userAgent,
        },
        timeout: 10000,
      });

      const refreshResponse: LoginResponse = await response.json();

      if (refreshResponse.accessToken && refreshResponse.refreshToken) {
        const newTokens = {
          accessToken: refreshResponse.accessToken,
          refreshToken: refreshResponse.refreshToken,
        };

        saveAuthTokens(newTokens);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Create a configured ky instance
export const apiClient = ky.create({
  prefixUrl: API_BASE_URL,
  timeout: 30000,
  retry: {
    limit: 2,
    methods: ['get', 'put', 'head', 'delete', 'options', 'trace'],
    statusCodes: [408, 413, 429, 500, 502, 503, 504],
  },
  hooks: {
    beforeRequest: [
      async request => {
        // 서버 다운 시 API 호출 차단 (health check 요청 제외)
        if (!getServerHealthy()) {
          throw new Error('API 서버에 연결할 수 없습니다.');
        }

        // Keycloak 토큰 우선, 없으면 레거시 토큰 사용
        // 토큰 갱신은 AuthProvider(onTokenExpired + 30초 interval)에서 관리하므로
        // 여기서는 현재 토큰만 설정
        if (keycloak.authenticated && keycloak.token) {
          request.headers.set('Authorization', `Bearer ${keycloak.token}`);
        } else {
          // 레거시 폴백
          const token = getAccessToken();
          if (token) {
            request.headers.set('Authorization', `Bearer ${token}`);
          }
        }

        // Add content-type for JSON requests
        // (FormData 업로드는 브라우저가 설정한 multipart boundary를 보존해야 하므로 덮어쓰지 않는다)
        if (
          request.body &&
          typeof request.body === 'object' &&
          !request.headers.get('Content-Type')?.startsWith('multipart/')
        ) {
          request.headers.set('Content-Type', 'application/json');
        }
      },
    ],
    afterResponse: [
      async (request, options, response) => {
        // 403 PENDING_APPROVAL 처리
        if (response.status === 403) {
          try {
            const body = await response.clone().json();
            if (body.error === 'PENDING_APPROVAL') {
              if (typeof window !== 'undefined' && !window.location.pathname.includes('/pending-approval')) {
                window.location.href = '/pending-approval';
              }
              return response;
            }
          } catch {
            // not JSON, ignore
          }
        }

        // 401 인증 실패 처리
        if (response.status === 401) {
          // 재시도 요청이면 무한 루프 방지
          if (request.headers.get('X-Retry-After-401')) {
            clearAuthTokens();
            if (typeof window !== 'undefined' && !window.location.pathname.includes('/pending-approval')) {
              keycloak.login();
            }
            return response;
          }

          // Keycloak 인증 상태면 토큰 갱신 후 1회 재시도
          if (keycloak.authenticated) {
            try {
              await keycloak.updateToken(0);
              if (keycloak.token) {
                const newHeaders = new Headers(request.headers);
                newHeaders.set('Authorization', `Bearer ${keycloak.token}`);
                newHeaders.set('X-Retry-After-401', 'true');
                return ky(request, { ...options, headers: newHeaders });
              }
            } catch {
              // 토큰 갱신 실패 - 재로그인 필요
            }
            clearAuthTokens();
            keycloak.login();
            return response;
          }

          // 레거시 인증 모드
          if (request.url.includes('/auth/refresh')) {
            clearAuthTokens();
            keycloak.login();
            return response;
          }

          const refreshSuccess = await refreshAccessToken();
          if (refreshSuccess) {
            const newToken = getAccessToken();
            if (newToken) {
              const newHeaders = new Headers(request.headers);
              newHeaders.set('Authorization', `Bearer ${newToken}`);
              return ky(request, { ...options, headers: newHeaders });
            }
          }

          clearAuthTokens();
          keycloak.login();
        }

        return response;
      },
    ],
    beforeError: [
      async error => {
        // ky 의 기본 message 는 "Request failed with status code 400" 형태라 서버가 돌려준 사유가
        // 사라진다. 응답 본문의 message(JSON) 또는 평문 본문(text/plain, WebDomainExceptionHandler의
        // IllegalArgumentException 등)으로 갈아끼워 화면 토스트에 그대로 노출되게 한다.
        // (예: "발행기간의 시작월과 종료월은 같아야 합니다", "회사의 마지막 역할세트 관리 권한은 제거할 수 없습니다")
        // 4xx 만 대상으로 한다. 5xx 본문에는 내부 예외 메시지가 담길 수 있어 그대로 노출하면 안 된다.
        const status = error.response?.status ?? 0;
        const isPlainText = (error.response?.headers.get('content-type') ?? '').startsWith('text/plain');
        const serverMessage =
          status >= 400 && status < 500
            ? await Promise.resolve()
                .then(() =>
                  isPlainText
                    ? error.response.clone().text().then(t => t.trim())
                    : error.response.clone().json().then((body: unknown) => {
                        const value =
                          body && typeof body === 'object' && 'message' in body
                            ? (body as { message?: unknown }).message
                            : '';
                        // 객체가 오면 "[object Object]" 가 되므로 문자열일 때만 쓴다
                        return typeof value === 'string' ? value : '';
                      })
                )
                .catch(() => '')
            : '';

        // Log errors for debugging
        console.error('API Error:', {
          url: error.request.url,
          method: error.request.method,
          status: error.response?.status,
          message: serverMessage || error.message,
        });

        if (serverMessage) {
          error.message = serverMessage;
        }
        return error;
      },
    ],
  },
});

// Helper function to handle API responses
export const handleApiResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `HTTP ${response.status}: ${response.statusText}`
    );
  }

  return response.json();
};

// Helper function for authenticated requests
export const authenticatedRequest = (token: string) => {
  return apiClient.extend({
    hooks: {
      beforeRequest: [
        request => {
          request.headers.set('Authorization', `Bearer ${token}`);
        },
      ],
    },
  });
};

// Error types for better error handling
export interface ApiError {
  message: string;
  status?: number;
  details?: unknown;
}

export class ApiException extends Error {
  constructor(
    message: string,
    public status?: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiException';
  }
}
